import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ResourceSet } from './resource-set';
export { ResourceSet };
import { resourceSetSchema } from '@schemas/business-objects/resource-set';
import { ActivityItemFormArray } from './activity-item-form-group';
import { ResourceCategoryFormArray } from './resource-category-form-group';
import { UserFormGroup } from './user-form-group';
import type { ExportImportConfig } from '../interface-definitions/index';
import type { Resource } from './resource';
import type { TextBox } from './text-box';

export class ResourceSetFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    locked: SchemaFormControl;
    uuid: SchemaFormControl;
    version: SchemaFormControl;
    status: SchemaFormControl;
    createdAt: SchemaFormControl;
    publishedAt: SchemaFormControl;
    archivedAt: SchemaFormControl;
    resourceCategories: ResourceCategoryFormArray;
    activityItems: ActivityItemFormArray;
    createdById: SchemaFormControl;
    createdBy: UserFormGroup;
    publishedById: SchemaFormControl;
    publishedBy: UserFormGroup;
    archivedById: SchemaFormControl;
    archivedBy: UserFormGroup;
  };

  declare value: Partial<ResourceSet>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(resourceSetSchema, ResourceSet, data, options);
  }

  get object(): ResourceSet {
    return new ResourceSet(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'resourceCategories':
        return new ResourceCategoryFormArray();
      case 'activityItems':
        return new ActivityItemFormArray();
      case 'createdBy':
        return new UserFormGroup();
      case 'publishedBy':
        return new UserFormGroup();
      case 'archivedBy':
        return new UserFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ResourceSet[]> {
    return ResourceSet.find(filter);
  }

  static async findOne(filter?: any): Promise<ResourceSet | null> {
    return ResourceSet.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<ResourceSet> {
    return ResourceSet.findById(id, filter);
  }

  static async create(data: Partial<ResourceSet>): Promise<ResourceSet> {
    return ResourceSet.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<ResourceSet>,
  ): Promise<ResourceSet> {
    return ResourceSet.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return ResourceSet.deleteById(id);
  }

  async export(): Promise<any> {
    const instance = new ResourceSet(this.getRawValue());
    return instance.export();
  }

  static async exportMany(body: any): Promise<any> {
    return ResourceSet.exportMany(body);
  }

  async publish(): Promise<any> {
    const instance = new ResourceSet(this.getRawValue());
    return instance.publish();
  }

  async newVersion(): Promise<any> {
    const instance = new ResourceSet(this.getRawValue());
    return instance.newVersion();
  }

  async latestVersion(): Promise<any> {
    const instance = new ResourceSet(this.getRawValue());
    return instance.latestVersion();
  }

  static async findLatestVersions(
    filter: any,
    publishedOnly?: boolean,
  ): Promise<any[]> {
    return ResourceSet.findLatestVersions(filter, publishedOnly);
  }

  async archive(): Promise<any> {
    const instance = new ResourceSet(this.getRawValue());
    return instance.archive();
  }

  async dataForCopy(): Promise<any> {
    const instance = new ResourceSet(this.getRawValue());
    return instance.dataForCopy();
  }
}

export class ResourceSetFormArray extends PersistedFormArray {
  readonly entityName = 'ResourceSet' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(resourceSetSchema, ResourceSet, options);
  }

  protected override createItemGroup() {
    return new ResourceSetFormGroup();
  }

  get array(): ResourceSet[] {
    return this.controls.map((group: ResourceSetFormGroup) => group.object);
  }
}

export class ResourceSetPersistedArray extends PersistedArray<ResourceSet> {
  readonly entityName = 'ResourceSet' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ResourceSet, options);
  }
}
